/**
 * UserNavWrapper Component
 *
 * Smart wrapper that displays the appropriate user navigation based on:
 * - No session: Login button
 * - Session but no contact details: UserNavSimple (with "Complete registration")
 * - Session with contact details: Full UserNav
 */

"use client"

import { Button } from "@/components/ui/Button"
import { useUser } from "@/contexts/UserContext"
import { LogIn } from "lucide-react"
import { useRouter } from "next/navigation"
import * as React from "react"
import UserNav from "./UserNav"
import UserNavSimple from "./UserNavSimple"

export default function UserNavWrapper() {
  const router = useRouter()
  const { email, name, claimed, _raw } = useUser()
  const [mounted, setMounted] = React.useState(false)
  const [azureSession, setAzureSession] = React.useState<{
    email: string
    name: string
  } | null>(null)

  React.useEffect(() => {
    setMounted(true)

    // Check for Azure B2C session even if no contact details
    // This is needed because useUser() may return null email if contact details don't exist
    const checkAzureSession = async () => {
      try {
        const res = await fetch("/api/me", { cache: "no-store" })
        if (res.ok) {
          const data = await res.json()
          if (data?.user) {
            setAzureSession({
              email: data.user.email || data.user.mail || "",
              name: data.user.name || data.user.given_name || "",
            })
          }
        }
      } catch (error) {
        console.error("[UserNavWrapper] Failed to check Azure session:", error)
      }
    }

    checkAzureSession()
  }, [])

  if (!mounted) return null

  // Case 1: Has active contact with complete details → Full UserNav
  // claimed is already calculated from status === 'active' in UserContext
  if (email && claimed && _raw?.identity) {
    return <UserNav />
  }

  // Case 2: Has Azure session but contact not active or no contact details → Simple UserNav
  // Use azureSession if available, otherwise fallback to context email/name
  const sessionEmail = azureSession?.email || email
  const sessionName = azureSession?.name || ""

  if (sessionEmail) {
    return <UserNavSimple userName={sessionName} userEmail={sessionEmail} />
  }

  // Case 3: No session → Login button
  const handleLogin = async () => {
    try {
      // Utiliser la page actuelle comme returnUrl
      const currentPath =
        typeof window !== "undefined" ? window.location.pathname : "/"

      const response = await fetch("/api/auth/signin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          returnUrl: currentPath,
          silentSSO: false,
        }),
      })

      const data = await response.json()

      if (data.authUrl) {
        window.location.href = data.authUrl
      } else if (data.authenticated) {
        window.location.href = currentPath
      }
    } catch (error) {
      console.error("[UserNavWrapper] Login error:", error)
      router.push("/signin")
    }
  }

  return (
    <Button variant="default" size="sm" onClick={handleLogin} className="ml-2">
      <LogIn className="h-4 w-4 md:mr-2" />
      <span className="hidden md:inline">Login</span>
    </Button>
  )
}
